import { useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Search,
  Filter,
  Plus,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Trophy,
  Flag,
  MapPin,
  RefreshCw,
  AlertCircle,
  User,
  Star,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useApi } from '../../contexts/ApiContext';
import { useSeries } from '../../App';

// Status color mapping - light theme
const statusColors = {
  'Active': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Current': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Inactive': 'bg-slate-100 text-slate-600 border-slate-200',
  'Retired': 'bg-red-100 text-red-700 border-red-200',
  'Suspended': 'bg-orange-100 text-orange-700 border-orange-200',
};

function CompetitorCard({ driver, seriesSlug }) {
  const statusColor = statusColors[driver.status] || 'bg-slate-100 text-slate-600';

  return (
    <Link
      to={`/${seriesSlug}/competitors/${driver.driver_ID}`}
      className="block"
    >
      <div className="p-2.5 rounded bg-white border border-slate-200 hover:border-wrg-blue-300 hover:shadow-sm transition-all group">
        <div className="flex items-center gap-3">
          {/* Avatar/Headshot */}
          <div className="flex-shrink-0">
            {driver.headshot_url ? (
              <img
                src={driver.headshot_url}
                alt={driver.driver_name}
                className="w-10 h-10 rounded object-cover bg-slate-100"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-400" />
              </div>
            )}
          </div>

          {/* Driver Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm font-semibold text-slate-800 group-hover:text-wrg-blue-600 transition-colors truncate">
                  {driver.driver_name}
                </h3>
                {driver.status && (
                  <Badge size="sm" className={`flex-shrink-0 ${statusColor}`}>
                    {driver.status}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
              {(driver.hometown || driver.home_state) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {driver.hometown}{driver.hometown && driver.home_state && ', '}{driver.home_state}
                </span>
              )}
              {driver.wins > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Trophy className="w-3 h-3" />
                  {driver.wins}
                </span>
              )}
              {driver.starts > 0 && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Flag className="w-3 h-3" />
                  {driver.starts}
                </span>
              )}
              {driver.team_name && (
                <span className="truncate text-slate-400">{driver.team_name}</span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-wrg-blue-600 transition-colors flex-shrink-0" />
        </div>
      </div>
    </Link>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-xs text-slate-500">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        {startPage > 1 && (
          <>
            <Button
              variant={1 === currentPage ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => onPageChange(1)}
            >
              1
            </Button>
            {startPage > 2 && <span className="px-1.5 text-xs text-slate-400">...</span>}
          </>
        )}
        {pages.map((page) => (
          <Button
            key={page}
            variant={page === currentPage ? 'secondary' : 'ghost'}
            size="xs"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1.5 text-xs text-slate-400">...</span>}
            <Button
              variant={totalPages === currentPage ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function CompetitorsList() {
  const { seriesSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();
  const currentSeries = useSeries();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [showFilters, setShowFilters] = useState(false);

  const { data: statuses = [] } = useQuery({
    queryKey: ['driver-statuses'],
    queryFn: async () => {
      // Authenticated api wrapper, not raw fetch — endpoint requires a token.
      const data = await api.apiCall('/api/drivers/data/statuses');
      return Array.isArray(data) ? data : [];
    },
  });

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const seriesId = currentSeries?.dbId || currentSeries?.racingEntityId;

  const { data: driversData, isLoading: loading, error: driversError, refetch } = useQuery({
    queryKey: ['drivers', searchParams.toString(), seriesId],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: 25
      };

      const searchVal = searchParams.get('search');
      const statusVal = searchParams.get('status');
      if (searchVal) params.search = searchVal;
      if (statusVal) params.status_ID = statusVal;
      if (seriesId) params.wrg_series_ID = seriesId;

      const result = await api.drivers.getAll(params);
      if (!result.success) throw new Error(result.message || 'Failed to fetch competitors');
      return {
        drivers: result.data.drivers || [],
        pagination: result.data.pagination || { total: 0, page: 1, limit: 25, pages: 0 }
      };
    },
  });

  const drivers = driversData?.drivers || [];
  const pagination = driversData?.pagination || { total: 0, page: 1, limit: 25, pages: 0 };
  const error = driversError?.message || null;

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  // Quick stats
  const activeCount = drivers.filter(d => d.status === 'Active' || d.status === 'Current').length;
  const totalWins = drivers.reduce((sum, d) => sum + (d.wins || 0), 0);

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Competitors</h1>
            <p className="text-xs text-slate-500">
              {pagination.total} drivers in {currentSeries?.shortName || 'series'}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => refetch()}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Link to={`/${seriesSlug}/competitors/new`}>
              <Button size="sm" leftIcon={Plus}>
                Add Competitor
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card padding="compact">
          <CardContent className="p-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-wrg-blue-50 rounded">
                <Users className="w-4 h-4 text-wrg-blue-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{pagination.total}</div>
                <div className="text-2xs text-slate-500">Total Drivers</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card padding="compact">
          <CardContent className="p-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-emerald-50 rounded">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{activeCount}</div>
                <div className="text-2xs text-slate-500">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card padding="compact">
          <CardContent className="p-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-amber-50 rounded">
                <Trophy className="w-4 h-4 text-amber-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{totalWins}</div>
                <div className="text-2xs text-slate-500">Total Wins</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card padding="compact">
          <CardContent className="p-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-purple-50 rounded">
                <Star className="w-4 h-4 text-purple-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">
                  {drivers.filter(d => d.wins > 0).length}
                </div>
                <div className="text-2xs text-slate-500">Winners</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card padding="compact" className="mb-4">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={Search}
                size="sm"
              />
            </form>

            {/* Quick Filters */}
            <div className="flex items-center gap-1.5">
              <Button
                variant={showFilters ? 'secondary' : 'ghost'}
                size="xs"
                onClick={() => setShowFilters(!showFilters)}
                leftIcon={Filter}
              >
                Filters
                <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                size="sm"
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Competitors List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-2.5 rounded bg-white border border-slate-200 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-100 rounded w-1/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <Card padding="compact">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-md font-medium text-slate-800 mb-1.5">Error Loading Competitors</h3>
            <p className="text-xs text-slate-500 mb-3">{error}</p>
            <Button size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : drivers.length === 0 ? (
        <Card padding="compact">
          <CardContent className="p-6 text-center">
            <Users className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="text-md font-medium text-slate-800 mb-1.5">No Competitors Found</h3>
            <p className="text-xs text-slate-500 mb-3">
              {search ? 'Try adjusting your search or filters' : 'No drivers registered for this series yet'}
            </p>
            <Link to={`/${seriesSlug}/competitors/new`}>
              <Button size="sm" leftIcon={Plus}>
                Add Competitor
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {drivers.map((driver) => (
              <CompetitorCard key={driver.driver_ID} driver={driver} seriesSlug={seriesSlug} />
            ))}
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}
